import React, { useEffect, useMemo, useState } from 'react';
import {
  isOwnedCxHost,
  tenantSlugFromHost,
} from '../../domain/domainRouting';
import {
  resolveTenantRouting,
  TenantRoutingResolution,
} from '../../services/tenantRoutingService';
import {
  firstLoadableImageHref,
  setDocumentLink,
} from '../../branding/favicon';

const DEFAULT_APP_TITLE = 'Apruv CX Client';
const DEFAULT_DESCRIPTION = 'Apruv customer experience client for reviews, tipping, and follow-up workflows.';
const DEFAULT_ICON_HREF = '/favicon.ico';

function upsertMeta(name: string, content: string) {
  let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = name;
    document.head.appendChild(tag);
  }
  tag.content = content;
}

function buildManifestUrl(title: string, iconHref: string): string | null {
  if (typeof URL.createObjectURL !== 'function') return null;

  const manifest = {
    short_name: title,
    name: title,
    icons: [
      {
        src: iconHref,
        sizes: '192x192',
        type: 'image/png',
      },
      {
        src: iconHref,
        sizes: '512x512',
        type: 'image/png',
      },
    ],
    start_url: '.',
    display: 'standalone',
    theme_color: '#050520',
    background_color: '#ffffff',
  };

  return URL.createObjectURL(
    new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
  );
}

const TenantMetadata: React.FC = () => {
  const [tenant, setTenant] = useState<TenantRoutingResolution | null>(null);
  const [iconHref, setIconHref] = useState(DEFAULT_ICON_HREF);

  useEffect(() => {
    const host = window.location.host;
    if (!isOwnedCxHost(host)) {
      setTenant(null);
      return;
    }

    const tenantSlug = tenantSlugFromHost(host);
    if (!tenantSlug) {
      setTenant(null);
      return;
    }

    let cancelled = false;
    resolveTenantRouting(host, tenantSlug)
      .then((resolution) => {
        if (!cancelled) setTenant(resolution);
      })
      .catch(() => {
        if (!cancelled) setTenant(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!tenant) {
      setIconHref(DEFAULT_ICON_HREF);
      return;
    }

    let cancelled = false;
    firstLoadableImageHref([tenant.faviconUrl, tenant.companyImageUrl]).then(
      (resolvedHref) => {
        if (!cancelled) setIconHref(resolvedHref || DEFAULT_ICON_HREF);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [tenant]);

  const metadata = useMemo(() => {
    const companyName = tenant?.companyName?.trim();
    const title = companyName || DEFAULT_APP_TITLE;
    const description = companyName
      ? `${companyName} customer experience.`
      : DEFAULT_DESCRIPTION;

    return { description, iconHref, title };
  }, [iconHref, tenant]);

  useEffect(() => {
    document.title = metadata.title;
    upsertMeta('description', metadata.description);
    upsertMeta('application-name', metadata.title);
    setDocumentLink('icon', metadata.iconHref);
    setDocumentLink('apple-touch-icon', metadata.iconHref);

    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) existingManifest.remove();

    const manifestUrl = buildManifestUrl(metadata.title, metadata.iconHref);
    if (!manifestUrl) {
      setDocumentLink('manifest', '/manifest.json');
      return undefined;
    }

    const manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    manifestLink.href = manifestUrl;
    document.head.appendChild(manifestLink);

    return () => {
      manifestLink.remove();
      URL.revokeObjectURL(manifestUrl);
    };
  }, [metadata]);

  return null;
};

export default TenantMetadata;
